import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Trophy, Gamepad2, Flame } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { LandingNav } from "@/components/landing/LandingNav";
import { Footer } from "@/components/Footer";
import { ClipTile } from "@/pages/CreatorProfile";
import { GridSkeleton } from "@/components/Skeletons";

const Leaderboard = ({ lb }) => {
  const { t } = useI18n();
  return (
    <aside className="cx-card cx-brackets rounded-2xl p-5 lg:sticky lg:top-28" data-testid="leaderboard">
      <div className="flex items-center gap-2 text-[#ffc94d]"><Trophy className="w-5 h-5" /><span className="font-display font-bold text-white">{t("lb.title")}</span></div>
      <p className="text-xs text-slate-500 mt-1 font-mono" data-testid="leaderboard-month">{lb?.month}</p>
      <ol className="mt-4 space-y-2">
        {(lb?.rows || []).map((r) => (
          <li key={r.rank} className="flex items-center gap-3 rounded-lg bg-white/[0.03] px-3 py-2" data-testid={`leaderboard-row-${r.rank}`}>
            <span className={`w-7 text-center font-display font-bold ${r.rank === 1 ? "text-[#ffc94d]" : r.rank <= 3 ? "text-[#c79bff]" : "text-slate-500"}`}>#{r.rank}</span>
            <span className="flex-1 min-w-0 text-sm text-white truncate">{r.creator}</span>
            <span className="text-xs font-mono text-[#3ad7ff] flex items-center gap-1"><Eye className="w-3 h-3" />{r.views}</span>
          </li>
        ))}
        {lb && !lb.rows.length && <li className="text-sm text-slate-500" data-testid="leaderboard-empty">{t("lb.empty")}</li>}
      </ol>
      <p className="text-[11px] text-slate-500 mt-4 leading-relaxed">{t("lb.how")}</p>
    </aside>
  );
};

export default function Vitrine() {
  const { t } = useI18n();
  const [game, setGame] = useState("");
  const [sort, setSort] = useState("recent");
  const [games, setGames] = useState([]);
  const [cards, setCards] = useState(null);
  const [lb, setLb] = useState(null);
  useEffect(() => {
    api.get("/gallery/games").then((r) => setGames(r.data)).catch(() => {});
    api.get("/leaderboard").then((r) => setLb(r.data)).catch(() => setLb({ month: "", rows: [] }));
  }, []);
  useEffect(() => { api.get("/gallery", { params: { game, sort, limit: 200 } }).then((r) => setCards(r.data)).catch(() => setCards([])); }, [game, sort]);
  const chip = (k, label, n) => (
    <button key={k || "all"} onClick={() => setGame(k)} data-testid={`vitrine-filter-${k || "all"}`}
      className={`rounded-full px-4 py-2 text-xs border transition-colors ${game === k ? "bg-[#2f7bff]/20 border-[#2f7bff]/60 text-white" : "border-white/10 text-slate-400 hover:text-white"}`}>
      {label}{n != null && <span className="ml-1.5 font-mono text-slate-500">{n}</span>}
    </button>
  );
  return (
    <div className="min-h-screen bg-[#04050a] text-white" data-testid="vitrine-page">
      <LandingNav />
      <main className="max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-20">
        <span className="cx-label">{t("vt.label")}</span>
        <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl mt-3"><span className="cx-chrome">{t("vt.title1")}</span><span className="cx-text-grad">{t("vt.title2")}</span></h1>
        <p className="text-slate-400 mt-4 max-w-2xl">{t("vt.sub")}</p>
        <div className="flex flex-wrap gap-2 mt-8" data-testid="vitrine-filters">
          <Gamepad2 className="w-4 h-4 text-slate-500 self-center mr-1" />
          {chip("", t("vt.all"))}
          {games.map((g) => chip(g.game, g.game, g.count))}
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="vitrine-sort">
          <Flame className="w-4 h-4 text-[#ff8a00] mr-1" />
          {[["recent", t("vt.sort_recent")], ["reactions", t("vt.sort_reactions")], ["views", t("vt.sort_views")]].map(([k, label]) => (
            <button key={k} onClick={() => setSort(k)} data-testid={`vitrine-sort-${k}`}
              className={`rounded-full px-4 py-1.5 text-xs border transition-colors ${sort === k ? "bg-[#ff8a00]/15 border-[#ff8a00]/60 text-white" : "border-white/10 text-slate-400 hover:text-white"}`}>{label}</button>
          ))}
        </div>
        <div className="grid lg:grid-cols-[1fr_320px] gap-8 mt-8 items-start">
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5" data-testid="vitrine-grid">
            {cards?.map((g, i) => <ClipTile key={g.share_id} g={g} i={i} prefix="vitrine" />)}
            {cards === null && <GridSkeleton testid="vitrine-skeleton" />}
            {cards && !cards.length && <p className="col-span-full text-slate-500" data-testid="vitrine-empty">{t("vt.empty")}</p>}
          </div>
          <Leaderboard lb={lb} />
        </div>
      </main>
      <Footer />
    </div>
  );
}
